import '../styles/globals.css';

import type {ReactElement, ReactNode} from 'react';
import type {NextPage} from 'next';
import type {AppProps} from 'next/app';
import type {EmotionCache} from '@emotion/react';

import * as React from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import CssBaseline from '@mui/material/CssBaseline';
import withTranslateRoutes, {translateUrl} from 'next-translate-routes';

import {useRouter} from 'next/router';
import {appWithTranslation} from 'next-i18next';
import {ThemeProvider} from '@mui/material/styles';
import {CacheProvider} from '@emotion/react';
import {NextSeo, SiteLinksSearchBoxJsonLd} from 'next-seo';

import {AppContextProvider, useAppContext} from '../contexts/AppContext';
import {CursorContextProvider} from '../contexts/CursorContext';
import {HighlightContextProvider} from '../contexts/HighlightContext';
import {ResizeContextProvider} from '../contexts/ResizeContext';
import {AuthenticationContextProvider} from '../contexts/AuthenticationContext';

import {getLocalizedPaths, translatePath, getLocalizedSlugs} from '../utils/localize-helpers';
import index from '../app/theme';
import {customOptions} from '../contexts/AppContext/custom-options';
import createEmotionCache from '../app/create-emotion-cache';
import {isEmpty} from '../utils/is-empty';
import * as iat from '../libraries/iat';
import * as gtm from '../libraries/gtm';
import * as gtag from '../libraries/gtag';
import * as fbq from '../libraries/fpixel';
import Ancestor from '../utils/ancestor';
import getConfig from 'next/config';

const NextNProgress = dynamic<any>(() => import('../components/ProgressBar').then((mod) => mod.NextNProgress), {
  ssr: false,
});

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

interface AppPropsWithLayout extends AppProps {
  Component: NextPageWithLayout;
  emotionCache?: EmotionCache;
  err?: object; // Todo: Get sentry type definition.
}

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

const { publicRuntimeConfig } = getConfig();
const basePath = publicRuntimeConfig.basePath || ''

function DefaultSeoOptions() {
  const appContext = useAppContext();
  const { noFollow, componentOptions } = appContext.useConfig();
  const url = `${process.env.PROJECT_URL?.replace(/\/+$/, '')}`;
  const router = useRouter();
  const languageAlternatives = getLocalizedPaths(router).map((path) => ({
    hrefLang: path.locale,
    href: `${url}${path.path}`,
  }));

  return (
    <>
      <NextSeo
        nofollow={noFollow ? true : false}
        noindex={noFollow ? true : false}
        openGraph={
          !componentOptions.component_default_options_og_is_disabled
            ? {
                url,
                locale: router.locale,
                type: 'website',
                site_name: `${customOptions.defaultTitle}`,
              }
            : undefined
        }
        twitter={{
          handle: `${customOptions.twitterHandle}`,
          site: `${customOptions.twitterSiteHandle}`,
          cardType: 'summary',
        }}
        facebook={{
          appId: `${customOptions.facebookTrackingId}`,
        }}
        canonical={componentOptions.component_facebook_og_url ? componentOptions.component_facebook_og_url : `${url}${basePath}${translatePath(router)}`}
        languageAlternates={languageAlternatives.map((locale) => ({
          hrefLang: locale.hrefLang,
          href: locale.href,
        }))}
        additionalMetaTags={[
          {
            name: 'application-build',
            content: `${new Date(Date.now()).toLocaleString()}`,
          },
        ]}
      />

      {!componentOptions.component_site_link_searchbox_json_ld_is_disabled && <SiteLinksSearchBoxJsonLd
        url={url}
        lastReviewed={`${new Date(Date.now()).toLocaleString()}`}
        potentialActions={[
          {
            target: `${url}${translateUrl('/search', router.locale || 'en')}?q`,
            queryInput: 'search_term_string',
          },
        ]}
      />}

      <NextNProgress
        color={index.palette.secondary.main}
        startPosition={0.3}
        stopDelayMs={200}
        height={3}
        showOnShallow={true}
        options={{showSpinner: false}}
        // nonce="my-nonce" // Todo: Set CSP correctly and generate nonce server side, before enabling this.
      />
    </>
  )
}

function MyApp(props: AppPropsWithLayout) {
  const {Component, emotionCache = clientSideEmotionCache, pageProps} = props;
  const router = useRouter();
  const appContext = useAppContext();
  const url = `${process.env.PROJECT_URL?.replace(/\/+$/, '')}`;
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [ancestorResizeHandler, setAncestorResizeHandler] = React.useState<Boolean>(false);
  const ancestor = new Ancestor();

  const languageAlternatives = getLocalizedPaths(router).map((path) => ({
    hrefLang: path.locale,
    href: `${url}${path.path}`,
  }));
  languageAlternatives.push({hrefLang: 'x-default', href: `${url}${translatePath(router)}`});

  // Use the layout defined at the page level, if available.
  // https://nextjs.org/docs/basic-features/layouts
  const getLayout = Component.getLayout ?? ((page) => page);

  // Handle route changes for tracking solutions.
  // This can be cause performance issue
  // THIS IS NOT WORKING !
  React.useEffect(() => {
    const handleRouteChange = (url: string) => {
      console.log("routerrouter123router", router.asPath)
      if (!router.asPath.includes('?') || (router.asPath.includes('?') && !isEmpty(router.query))) {
        gtag.pageview(url);
        gtm.pageview(url);
        fbq.pageview();
        iat.pageview();
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);

  React.useEffect(() => {
    setTimeout(() => ancestor.postDimensions(false, containerRef.current), 5000);
  }, [router.pathname]);

  return (
    <ResizeContextProvider
      containerRef={containerRef}
      ancestorResizeHandler={ancestorResizeHandler}
      setAncestorResizeHandler={setAncestorResizeHandler}
    >
      <CacheProvider value={emotionCache}>
        <Head>
          <meta
            name='viewport'
            content='width=device-width, initial-scale=1'
          />
        </Head>
        <CursorContextProvider enable={false}>
          <HighlightContextProvider
            enable={false}
            enableVariants={[]}
          >
            <AppContextProvider>
              <AuthenticationContextProvider>
                <ThemeProvider theme={index}>
                  <CssBaseline />
                  <DefaultSeoOptions />
                  <div style={{ height: "100%" }} ref={containerRef}>{getLayout(<Component {...pageProps} />)}</div>
                </ThemeProvider>
              </AuthenticationContextProvider>
            </AppContextProvider>
          </HighlightContextProvider>
        </CursorContextProvider>
      </CacheProvider>
    </ResizeContextProvider>
  );
}

export default withTranslateRoutes(JSON.parse(`{"debug":false,"defaultLocale":"it","locales":["it","en"],"routesTree":{"name":"","paths":{"default":""},"children":[{"name":"brands","paths":{"default":"brands","en":"brands","it":"marche","pt":"marcas","nl":"merken"}},{"name":"categories","paths":{"default":"categories","en":"categories","it":"categorie","pt":"categorias","nl":"categorieen"}},{"name":"collections","paths":{"default":"collections","en":"collections","it":"collezioni","pt":"colecoes","nl":"collecties"}},{"name":"contact","paths":{"default":"contact","en":"contact","it":"contattaci","pt":"contactu","nl":"contact"}},{"name":"index","paths":{"default":"index"}},{"name":"search","paths":{"default":"search","en":"search","it":"ricerca","pt":"procurar","nl":"zoeken"}},{"name":"sitemap.xml","paths":{"default":"sitemap.xml"}},{"name":"account","paths":{"default":"account"},"children":[{"name":"addKidModal","paths":{"default":"addKidModal"}},{"name":"index","paths":{"default":"index"}}]},{"name":"brand","paths":{"default":"brand","en":"brand","it":"marca","pt":"marca","nl":"merk"},"children":[{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"category","paths":{"default":"category","en":"category","it":"categoria","pt":"categoria","nl":"categorie"},"children":[{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"collection","paths":{"default":"collection","en":"collection","it":"collezione","pt":"colecao","nl":"collectie"},"children":[{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"game","paths":{"default":"game","en":"play","pt":"jogar","it":"gioca","nl":"spel"},"children":[{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"legal","paths":{"default":"legal","en":"legal","it":"legalli","pt":"juridico","nl":"policies"},"children":[{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"parenting-control","paths":{"default":"parenting-control"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"profiles","paths":{"default":"profiles"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"pinModal","paths":{"default":"pinModal"}}]},{"name":"subscription-plan","paths":{"default":"subscription-plan"},"children":[{"name":"index","paths":{"default":"index"}}]}]},"fallbackLng":{}}`), appWithTranslation(MyApp));
